/**
 * useFilesPicker - open the Files picker and await the pick (F-DMS).
 *
 *   const { pickFiles, picker } = useFilesPicker();
 *   ...
 *   const picked = await pickFiles({ accept: ['pdf', 'docx'] });
 *   if (picked) attach(picked.map((f) => f.uuid));
 *   ...
 *   return <>{...}{picker}</>;
 *
 * `pickFiles` resolves with the picked Files (`PickedFile[]`) or `null` when
 * the user cancels. `picker` is the modal element - render it once anywhere
 * in the component; it renders nothing while closed. It is rendered by the
 * caller (not portalled from a provider) so it sits inside the same React
 * tree and sees the Google / Microsoft contexts the external tabs need.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import FilesPickerModal from './FilesPickerModal';
import type { FilesPickerOptions, PickedFile } from './filesPickerTypes';

export type PickFiles = (options?: FilesPickerOptions) => Promise<PickedFile[] | null>;

export interface UseFilesPickerResult {
  pickFiles: PickFiles;
  /** The modal. Render it once; it is inert while no pick is pending. */
  picker: React.ReactElement;
  isOpen: boolean;
}

interface PendingPick {
  options?: FilesPickerOptions;
  resolve: (files: PickedFile[] | null) => void;
}

export function useFilesPicker(): UseFilesPickerResult {
  const [pending, setPending] = useState<PendingPick | null>(null);
  const pendingRef = useRef<PendingPick | null>(null);

  const settle = useCallback((files: PickedFile[] | null) => {
    const current = pendingRef.current;
    pendingRef.current = null;
    setPending(null);
    current?.resolve(files);
  }, []);

  const pickFiles = useCallback<PickFiles>(
    (options) =>
      new Promise<PickedFile[] | null>((resolve) => {
        // A second call supersedes the first; the first resolves as cancelled.
        pendingRef.current?.resolve(null);
        const next = { options, resolve };
        pendingRef.current = next;
        setPending(next);
      }),
    [],
  );

  // Never leave a caller awaiting forever.
  useEffect(() => () => pendingRef.current?.resolve(null), []);

  const picker = (
    <FilesPickerModal
      isOpen={!!pending}
      options={pending?.options}
      onCancel={() => settle(null)}
      onConfirm={(files) => settle(files)}
    />
  );

  return { pickFiles, picker, isOpen: !!pending };
}

export default useFilesPicker;
